import { type ReactElement, useCallback, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import { useIndirectCosts } from '@/features/indirect-costs/hooks/useIndirectCosts'
import { useLinkedPaymentsCount } from '@/features/indirect-costs/hooks/useLinkedPaymentsCount'
import { ensureRecurringCosts } from '@/features/indirect-costs/api/indirectCostsService'
import { useStableOrder } from '../hooks/useStableOrder'
import { useViewportScrollAreaHeight } from '../hooks/useViewportScrollAreaHeight'
import { CostCard } from './CostCard'
import { CostNotNeededDropZone } from './CostNotNeededDropZone'
import { AddCostCard } from './AddCostCard'
import { ColumnMonthNav } from './ColumnMonthNav'

const PAGE_SIZE = 50

interface CostsColumnProps {
  year: number
  monthIndex: number
  onMonthChange: (year: number, monthIndex: number) => void
  isDualColumn: boolean
  isDragActive: boolean
  showCostNotNeededDropZone: boolean
  recentlyLinkedCostUuid?: string | null
  className?: string
}

export function CostsColumn({
  year,
  monthIndex,
  onMonthChange,
  isDualColumn,
  isDragActive,
  showCostNotNeededDropZone,
  recentlyLinkedCostUuid,
  className,
}: CostsColumnProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()
  const ensuredMonthsRef = useRef<Set<string>>(new Set())

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useIndirectCosts(year, monthIndex, PAGE_SIZE)

  const costs = useMemo(
    () => data?.pages.flatMap(p => p.content).filter(c => !c.subcategorySystem) ?? [],
    [data],
  )
  const orderedCosts = useStableOrder(costs, cost => cost.uuid, `${year}-${monthIndex}`)

  const costUuids = useMemo(() => orderedCosts.map(c => c.uuid), [orderedCosts])
  const { counts } = useLinkedPaymentsCount(costUuids)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const scrollAreaHeight = useViewportScrollAreaHeight(scrollAreaRef)

  // Ensure recurring costs for this month
  useEffect(() => {
    const yearMonth = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
    if (ensuredMonthsRef.current.has(yearMonth)) return
    ensuredMonthsRef.current.add(yearMonth)
    void ensureRecurringCosts(yearMonth).then(({ created }) => {
      if (created > 0) {
        void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      }
    })
  }, [year, monthIndex, queryClient])

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null)
  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  useEffect(() => {
    const el = sentinelRef.current
    const root = scrollAreaRef.current
    if (!el || !root) return
    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.1,
      root,
      rootMargin: '0px 0px 160px 0px',
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [handleIntersect, scrollAreaHeight])

  return (
    <div className={cn('min-w-0', className)}>
      <ColumnMonthNav
        label={t('page.tabIndirect')}
        year={year}
        monthIndex={monthIndex}
        onChange={onMonthChange}
      />

      <div className="mt-5 space-y-3">
        <AddCostCard year={year} monthIndex={monthIndex} />
      </div>

      <div
        ref={scrollAreaRef}
        data-kanban-scroll-area="true"
        className="mt-3 space-y-3 overflow-y-auto overscroll-contain px-1 pt-1 scrollbar-subtle"
        style={scrollAreaHeight ? { height: `${scrollAreaHeight}px` } : undefined}
      >
        {showCostNotNeededDropZone && (
          <CostNotNeededDropZone isDragActive={isDragActive} />
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-5" />
          </div>
        ) : orderedCosts.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No costs this month
          </div>
        ) : (
          orderedCosts.map((cost, i) => (
            <CostCard
              key={cost.uuid}
              index={i + 1}
              cost={cost}
              linkedCount={counts[cost.uuid]?.count ?? 0}
              isDualColumn={isDualColumn}
              isDragActive={isDragActive}
              recentlyLinked={recentlyLinkedCostUuid === cost.uuid}
            />
          ))
        )}

        <div ref={sentinelRef} className="h-4" />
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <Spinner className="size-4" />
          </div>
        )}
      </div>
    </div>
  )
}
